/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import type { ConnectionRef, Fwaas } from "./FwaasData";
import GenericButton from "@ui-components/Button/GenericButton";
import { ConnectionType, RENDER_ORDER, TAG_LABEL } from "./ConnectionsHelpers";
import GenericCard from "@ui-components/Card/GenericCard";

type Props = {
    row: Fwaas;
    onItemClick?: (type: ConnectionType, id: string, row: Fwaas) => void;
    onShowAll?: (row: Fwaas) => void;
    /** Show ALL items, no truncation (table ask) */
    full?: boolean;
    /** Wrap content in a card-like container */
    showCard?: boolean;
    /** Max height for the scroll area when showCard is true */
    scrollMaxHeight?: number;       // default 140
    cardVariant?: "primary" | "secondary" | "tertiary";
    cardShadow?: "flat" | "sm" | "md";  
    filterFn?: (type: ConnectionType, item: ConnectionRef) => boolean;
  };

export function ConnectionsCell({
    row,
    onItemClick,
    onShowAll,
    full = false,
    showCard = false,
    scrollMaxHeight = 140,
    cardVariant = "secondary",
    cardShadow = "sm",
    filterFn,
  }: Props) {
    
    const buckets = row.connections ?? {};
    const lines: JSX.Element[] = [];
    let anyHasMore = false;

      // Helper to render the label exactly once
  const renderLabel = (type: ConnectionType, item: ConnectionRef) => {
    const tag = TAG_LABEL[type];
    const idText = item.subscriptionId || item.id || "";

    if (type === "fwint" || type === "sdwanInt") {
      const portSub = item.connectedServiceSubId;
      return (
        <>
          <span className="font-medium">[{tag}] - </span>
          <span>{idText}</span>
          {portSub ? (
            <>
              <span> - </span>
              <span className="font-medium">[PORT] - </span>
              <span>{portSub}</span>
            </>
          ) : null}
        </>
      );
    }

    // Default: [TAG] - <id>
    return (
      <>
        <span className="font-medium">[{tag}] - </span>
        <span>{idText}</span>
      </>
    );
  };

  for (const type of RENDER_ORDER) {
    const items = (buckets as any)[type] as ConnectionRef[] | undefined;
    if (!items?.length) continue;

    const filtered = filterFn ? items.filter((it) => filterFn(type, it)) : items;
    if (!filtered.length) continue;

    const shown = full ? filtered : filtered.slice(0, 3);
    const hasMore = !full && filtered.length > 3;
    if (hasMore) anyHasMore = true;


    lines.push(
        <div
          key={type}
          className="text-sm leading-6 flex flex-wrap items-center gap-x-1"
        >
          {shown.map((item, idx) => {
            const displayText = item.subscriptionId || item.id;
            // const navId = item.id || item.subscriptionId;        // navigate with id
            const navId =
            (type === "fwint" || type === "sdwanInt") &&
            item.connectedServiceId != null
              ? String(item.connectedServiceId)
              : String(item.id || item.subscriptionId);

            return (
              <React.Fragment key={`${type}-${displayText}-${idx}`}>
                <button
                  type="button"
                  className="underline hover:no-underline focus:outline-none text-semantic-text-brand"
                  onClick={() => onItemClick?.(type, String(navId), row)}
                  title={`${type} ${displayText}`}
                >
                {renderLabel(type, item)}
                </button>
                {idx < shown.length - 1 ? " , " : null}
              </React.Fragment>
            );
          })}
          {hasMore ? " ..." : null}
        </div>
  
      );
    }
    if (!lines.length) return <span className="text-gray-400">-</span>;

    const body = <div className="space-y-1">{lines}</div>;

    const shouldShowButton = Boolean(onShowAll) && (full ? true : anyHasMore);

    const inner = (
        <div className="flex items-start justify-between gap-3">
          <div
            className="flex-1 min-w-0"
            style={{ maxHeight: scrollMaxHeight, overflowY: "auto" }}
          >
            {body}
          </div>
          {shouldShowButton ? (
            <div className="shrink-0">
              <GenericButton
                size="sm"
                variant="secondary"
                onClick={() => onShowAll?.(row)}
                label="See all"
              />
            </div>
          ) : null}
        </div>
      );
  
    if (!showCard) return inner;
    // Card container with vertical scroll
    return (
        <GenericCard
        variant={cardVariant}
        shadow={cardShadow}
        isCollapsible={false}
        hasBorder
      >
        <div className="p-5">
            {inner}
        </div>
      </GenericCard>
    );
}
